import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getLadingPages,
  createLandingPage,
  deleteLandingPage,
  updateLandingPage,
} from "../../api/landingPageApi";
import PageLoader from "../../components/pageLoader";
import ToastContainer from "../../components/toastContainer";
import Toast from "../../components/toast";
import { ChevronDownIcon, ClipboardIcon, PencilIcon, TrashIcon } from "@heroicons/react/outline";
import DeleteConfirmationModal from "../../components/deleteModal";
import LandingPageFormModal from "./landingPageFormModal";

const LandingPages = () => {
  const [ladingPages, setLandingPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const [deleteLandingPageId, setDeleteLandingPageId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);

  const [editingLandingPage, setEditingLandingPage] = useState(null);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);

  const LIMIT = 10;

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [copied, setCopied] = useState(null);

  const totalPages = Math.ceil(total / LIMIT);

  const statusStyles = {
    active: "bg-emerald-500/10 text-emerald-400",
    paused: "bg-amber-500/10 text-amber-400",
    closed: "bg-red-500/10 text-red-400",
  };

  const statusClass = (status) =>
    statusStyles[(status || "").toLowerCase()] ||
    "bg-slate-500/10 text-slate-400";

  const fetchLandingPages = async () => {
    try {
      setLoading(true);

      const offset = (page - 1) * LIMIT;

      const data = await getLadingPages(LIMIT, offset);
      const list = Array.isArray(data) ? data : data.results || [];
      setTotal(data.count || 0);
      setLandingPages(list);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLandingPage = async () => {
    try {
      setDeleting(true);
      await deleteLandingPage(deleteLandingPageId);

      setDeleteLandingPageId(null);

      // Refresh companies list
      const offset = (page - 1) * LIMIT;
      const data = await getLadingPages(LIMIT, offset);
      setLandingPages(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleLandingPageSubmit = async (values) => {
    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("name", values.name);
      formData.append("title", values.title);
      formData.append("info", values.info);

      formData.append("is_active", true);

      if (values.flyer instanceof File) {
        formData.append("flyer", values.flyer);
      }

      values.map_latitude &&
        formData.append("map_latitude", Number(values.map_latitude).toFixed(6));
      values.map_longitude &&
        formData.append(
          "map_longitude",
          Number(values.map_longitude).toFixed(6),
        );
      formData.append("map_address", values.map_address);

      formData.append("whatsapp_link", values.link);
      formData.append("short_link", values.short_link);

      if (editingLandingPage) {
        await updateLandingPage(editingLandingPage.id, formData);
      } else {
        await createLandingPage(formData);
      }

      setEditingLandingPage(null);
      setShowAddModal(false);

      // Refresh list
      const offset = (page - 1) * LIMIT;
      const data = await getLadingPages(LIMIT, offset);
      setLandingPages(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (text, type, id) => {
    if (!text) return;

    await navigator.clipboard.writeText(text);

    setCopied(`${type}-${id}`);

    setTimeout(() => setCopied(null), 2000);
  };

  const handleStatusChange = async () => {
    try {
      setStatusSaving(true);
      await updateLandingPage(status.id, {
        status: newStatus
      });
      setStatus(null);
      fetchLandingPages();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setStatusSaving(false);
    }
  };

  useEffect(() => {
    fetchLandingPages();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Landing Pages</h1>
          <p className="text-sm text-slate-400">
            Create & Manage Landing Pages
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-sm font-medium transition"
        >
          + Add Landing Page
        </button>
      </div>

      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 text-slate-400">
            <tr>
              <th className="px-6 py-3 text-center">Event Name</th>
              <th className="px-6 py-3 text-center">Created at/ by</th>
              <th className="px-6 py-3 text-center">No of Submissions</th>
              <th className="px-6 py-3 text-center">Page Link</th>
              <th className="px-6 py-3 text-center">Short Link</th>
              <th className="px-6 py-3 text-center">Status</th>
              <th className="px-6 py-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {ladingPages.map((lp) => (
              <tr
                key={lp.id}
                className="border-b text-center border-white/5 hover:bg-slate-800/40 transition cursor-pointer"
              >
                <td className="px-6 py-4 text-slate-300">{lp.name || "-"}</td>
                <td className="px-6 py-4 text-slate-300">
                  {lp.created_at || "-"}
                </td>

                <td className="px-6 py-4 text-slate-300">
                  <span
                    onClick={() =>
                      navigate(`/dashboard/landing-pages/${lp.id}/submissions`)
                    }
                    className="text-pink-500 hover:underline cursor-pointer"
                  >
                    {lp.registration_count || 0}
                  </span>
                </td>
                <td className="py-2">
                  <div className="flex items-center justify-center gap-3">
                    <a
                      href={lp.page_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-pink-500 hover:underline"
                    >
                      View
                    </a>
                    |
                    <button
                      onClick={() => handleCopy(lp.page_link, "page", lp.id)}
                      className={`transition ${
                        copied === `page-${lp.id}`
                          ? "text-green-400"
                          : "text-slate-400 hover:text-white"
                      }`}
                      title="Copy link"
                    >
                      <ClipboardIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
                <td className="py-2">
                  <div className="flex items-center justify-center gap-3">
                    <a
                      href={lp.short_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-pink-500 hover:underline"
                    >
                      View
                    </a>
                    |
                    <button
                      onClick={() => handleCopy(lp.short_link, "short", lp.id)}
                      className={`transition ${
                        copied === `short-${lp.id}`
                          ? "text-green-400"
                          : "text-slate-400 hover:text-white"
                      }`}
                      title="Copy link"
                    >
                      <ClipboardIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
                {/* <td className="px-6 py-4 text-slate-300">
                  {lp.is_active ? "Active" : "Inactive" || "-"}
                </td> */}
                <td className="px-3 py-4 text-sm text-slate-300">
                  <div className="flex items-center justify-center gap-1">
                    <span
                      className={`px-2.5 py-0.5 rounded-md text-xs font-medium ${statusClass(lp.status)}`}
                    >
                      {lp.status || "-"}
                    </span>
                    <button
                      onClick={() => setStatus(lp)}
                      className="p-1 rounded text-slate-500 hover:text-white hover:bg-slate-700 transition"
                      title="Change status"
                    >
                      <ChevronDownIcon className="w-3 h-3" />
                    </button>
                  </div>
                </td>
                <td className="py-2 text-center space-x-3">
                  <button
                    onClick={() => {
                      setEditingLandingPage(lp);
                    }}
                    className="text-slate-400 hover:text-white"
                  >
                    <PencilIcon className="w-4 h-4 inline" />
                  </button>
                  <button
                    onClick={() => {
                      setDeleteLandingPageId(lp.id);
                    }}
                    className="text-red-400 hover:text-red-500"
                  >
                    <TrashIcon className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}

            {!ladingPages.length && (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-10 text-center text-slate-400"
                >
                  No Landing Pages found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex justify-end">
          {totalPages > 1 && (
            <div className="flex items-center gap-3 px-6 py-4 text-sm text-slate-400">
              {/* Previous */}
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-slate-800 text-white"
          }`}
              >
                Previous
              </button>

              {/* Pages */}
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
                ${
                  page === pageNumber
                    ? "bg-pink-600 text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
                    >
                      {pageNumber}
                    </button>
                  ),
                )}
              </div>

              {/* Next */}
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === totalPages
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-slate-800 text-white"
          }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Status Change Modal ── */}
      {status && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-xl shadow-xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-white/10">
              <h2 className="text-white font-semibold text-sm">
                Change Status
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">{status.name}</p>
            </div>

            {/* Options */}
            <div className="px-6 py-4 space-y-2">
              {["active", "paused", "closed"].map((s) => (
                <label
                  key={s}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition ${
                    newStatus === s
                      ? "border-pink-500/50 bg-pink-500/10"
                      : "border-white/10 hover:bg-slate-800"
                  }`}
                >
                  <input
                    type="radio"
                    name="admin_status"
                    value={s}
                    checked={newStatus === s}
                    onChange={() => setNewStatus(s)}
                    className="accent-pink-500"
                  />
                  <span
                    className={`text-xs font-medium capitalize px-2.5 py-0.5 rounded-md ${statusClass(s)}`}
                  >
                    {s.replace("_", " ")}
                  </span>
                </label>
              ))}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setStatus(null)}
                className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusChange}
                disabled={statusSaving || newStatus === status.admin_status}
                className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {statusSaving ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      <LandingPageFormModal
        isOpen={showAddModal || !!editingLandingPage}
        onClose={() => {
          setShowAddModal(false);
          setEditingLandingPage(null);
        }}
        onSubmit={handleLandingPageSubmit}
        loading={saving}
        mode={editingLandingPage ? "edit" : "create"}
        initialData={editingLandingPage}
      />

      <DeleteConfirmationModal
        isOpen={!!deleteLandingPageId}
        description="Are you sure you want to delete this landing page?  This action cannot be undone."
        onClose={() => setDeleteLandingPageId(null)}
        onConfirm={handleDeleteLandingPage}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default LandingPages;
